/**
 * Escapa antes de interpolar en HTML.
 *
 * El nombre de la carta lo escribe quien pega la lista, pero además puede llegar
 * en la URL (`?lista=`, ADR 153): `recibirListaDeLaUrl` lo precarga y lo busca
 * solo, así que basta con que alguien abra un enlace preparado para que el texto
 * llegue hasta estos `innerHTML`. Todo lo que se interpole —contenido de
 * elemento y valor de atributo— pasa por aquí.
 *
 * Se escapan las cinco entidades, no solo `<`: dentro de un atributo, una
 * comilla suelta se sale del atributo sin necesidad de abrir una etiqueta.
 */
function escaparHtml(valor) {
  return String(valor === null || valor === undefined ? '' : valor)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Eventos de producto (ADR 150). `vault-eventos.js` lo define y lo carga el
 * header, así que está disponible en toda la tienda — pero se comprueba igual:
 * medir nunca puede tumbar la pantalla.
 *
 * Solo dispara en `the-vault.mx`; desde el servidor de desarrollo el helper se
 * calla solo para no ensuciar las métricas de producción.
 */
function medir(evento, destino, metadata) {
  if (typeof window.vaultEvento !== 'function') return;
  window.vaultEvento(evento, destino === undefined || destino === null ? null : String(destino), metadata);
}

class DeckBuilder {
  constructor() {
    this.container = document.querySelector('.deck-builder');
    this.textarea = document.getElementById('deckList');
    this.searchButton = document.querySelector('.deck-builder__search');
    this.cardsGrid = document.querySelector('.deck-builder__cards-grid');
    this.listContent = document.querySelector('.deck-builder__list-content');
    this.addToCartButton = document.querySelector('.deck-builder__add-to-cart');
    // El Bounty vive junto al botón de carrito: son las dos salidas de la
    // lista. Agregas lo que hay, o pides que te consigan lo que falta.
    this.bountySlot = document.createElement('div');
    this.bountySlot.className = 'deck-builder__bounty';
    if (this.addToCartButton && this.addToCartButton.parentNode) {
      this.addToCartButton.parentNode.insertBefore(this.bountySlot, this.addToCartButton);
    }
    this.progressBar = document.querySelector('.deck-builder__progress-bar');
    this.listTitle = document.querySelector('.deck-builder__list-title');
    this.copyMissingButton = document.querySelector('.deck-builder__copy-missing');
    this.avisoElemento = document.querySelector('.deck-builder__aviso');
    this.barra = document.querySelector('.deck-builder__barra');

    this.preferenciasElemento = document.querySelector('.deck-builder__preferencias');

    /** Líneas que el parser no pudo leer. Se le dicen al usuario, no se tiran. */
    this.lineasIgnoradas = [];

    /**
     * Cómo elegir la versión de cada carta, para toda la lista.
     *
     * `condicion` es el comportamiento de siempre y sigue siendo el default:
     * cambiarlo por su cuenta habría empeorado el problema de que la búsqueda
     * difusa mete cartas ajenas. Pero cuando el usuario pide explícitamente el
     * precio más bajo, dárselo no tiene ese riesgo — está eligiendo, no
     * adivinando el sistema por él. En una lista de 40 cartas esa diferencia son
     * miles de pesos.
     */
    this.preferencias = { orden: 'condicion', soloIngles: false, sinFoil: false };

    /**
     * La única fuente de verdad de la pantalla de resultados.
     *
     * Antes no había ninguna: la lista, la reja de imágenes y el total se
     * pintaban por separado en una pasada y después nadie los volvía a tocar.
     * El checkbox no cambiaba nada —su estado solo se leía al momento de mandar
     * al carrito—, así que desmarcar una carta dejaba el total idéntico y la
     * imagen puesta. Tres vistas del mismo hecho, contándolo distinto.
     *
     * Ahora todo lo que se ve se deriva de aquí, y cualquier control que quite
     * o agregue cartas solo tiene que tocar `seleccionada` y volver a pintar.
     *
     * `seleccionada` quiere decir "la quiero", la tengamos o no: de una carta
     * marcada se lleva lo que hay y lo que falta va al Bounty; una desmarcada
     * no va a ningún lado. Antes la casilla de una faltante estaba apagada, así
     * que lo que el usuario ya tenía se iba igual al Bounty sin forma de soltarlo.
     */
    this.resultados = [];

    /**
     * Lo que YA está en el carrito, por variante.
     *
     * Sin esto la pantalla calculaba la disponibilidad contra un carrito vacío,
     * pero `/cart/add.js` es aditivo: lo que ya tienes adentro cuenta contra el
     * stock. Buscar la misma lista dos veces terminaba en un 422 de Shopify
     * —"tu carrito ya tiene la cantidad máxima de este artículo"— que aquí se
     * traducía a "ya no está disponible". La carta sí estaba; ya era tuya.
     */
    this.enCarrito = new Map();

    /** Resultado del último envío al carrito, para poder pintarlo. */
    this.ultimoEnvio = null;

    /**
     * Bounty (ADR 236): la última cotización de lo que falta. `clave` dice de
     * QUÉ faltantes es; si la lista cambia, la cotización vieja ya no aplica y
     * se vuelve a pedir. `vault-bounty.js` hace las cuentas y el modal.
     */
    this.bounty = { clave: null, cotizacion: null, temporizador: null };

    this.init();
  }

  init() {
    if (!this.textarea || !this.searchButton) {
      console.error('Required elements not found');
      return;
    }

    this.searchButton.addEventListener('click', () => this.parseDeckList());

    this.addToCartButton.addEventListener('click', () => this.addToCart());
    this.guardarButton = document.querySelector('.deck-builder__guardar');
    if (this.guardarButton) this.guardarButton.addEventListener('click', () => this.guardarCarrito());

    // Delegación en vez de un listener por tarjeta: la lista y la reja se
    // vuelven a pintar en cada cambio, así que enganchar elemento por elemento
    // obligaría a re-enganchar en cada pasada.
    this.listContent.addEventListener('change', evento => {
      // El `<input type="number">` avisa en `change`, no en `input`: se entera
      // al salir del campo o al dar Enter, nunca en cada tecla. Es a propósito
      // — cada cambio repinta la lista entera, y repintar mientras alguien
      // teclea le borraría el número a media escritura.
      const campo = evento.target.closest('input[data-cantidad]');
      if (campo) {
        this.cambiarCantidad(Number(campo.dataset.indice), Number(campo.value));
        return;
      }

      const casilla = evento.target.closest('input[type="checkbox"][data-indice]');
      if (!casilla) return;
      this.alternarSeleccion(Number(casilla.dataset.indice), casilla.checked);
    });

    this.bountySlot.addEventListener('click', evento => {
      if (evento.target.closest('[data-bounty-completar]')) this.completarMazo();
      else if (evento.target.closest('[data-bounty-abrir]')) this.abrirBounty();
    });

    this.listContent.addEventListener('click', evento => {

      const paso = evento.target.closest('.deck-builder__paso');
      if (paso) {
        const indice = Number(paso.dataset.indice);
        const resultado = this.resultados[indice];
        if (resultado) {
          this.cambiarCantidad(indice, this.cantidadDe(resultado) + Number(paso.dataset.paso));
        }
        return;
      }

      const otra = evento.target.closest('[data-otra-indice]');
      if (otra) {
        this.agregarOtraVersion(Number(otra.dataset.otraIndice));
        return;
      }

      const bountyCarta = evento.target.closest('[data-bounty-carta]');
      if (bountyCarta) {
        this.abrirBountyDeCarta(Number(bountyCarta.dataset.bountyCarta));
        return;
      }

      const version = evento.target.closest('.deck-builder__version');
      if (version) {
        this.elegirVersion(Number(version.dataset.indice), version.dataset.versionId);
        return;
      }

      const toggle = evento.target.closest('.deck-builder__versiones-toggle');
      if (!toggle) return;
      const resultado = this.resultados[Number(toggle.dataset.indice)];
      if (!resultado) return;
      resultado.versionesAbiertas = !resultado.versionesAbiertas;
      this.pintar();
    });

    this.cardsGrid.addEventListener('click', evento => {
      const tache = evento.target.closest('.deck-builder__card-remove');
      if (!tache) return;
      this.alternarSeleccion(Number(tache.dataset.indice), false, 'tache');
    });

    if (this.copyMissingButton) {
      this.copyMissingButton.addEventListener('click', () => this.copiarFaltantes());
    }

    if (this.preferenciasElemento) {
      this.preferenciasElemento.addEventListener('change', evento => {
        const control = evento.target;

        if (control.name === 'deck-orden') {
          this.preferencias.orden = control.value;
          medir('deck_price_order', control.value);
        } else if (control.dataset.filtro) {
          this.preferencias[control.dataset.filtro] = control.checked;
        } else {
          return;
        }

        // Cambiar la preferencia rehace la elección de TODAS las cartas, salvo
        // donde el usuario ya eligió a mano: esa decisión es más específica y
        // manda. `calcularCobertura` la respeta sola.
        this.recalcularTodo();
        this.pintar();
      });
    }

    this.medirChrome();
    // El layout todavía no existe en `DOMContentLoaded` —las medidas salen en 0—
    // así que se vuelve a medir en `load`, igual que hace `vault-hero.js`.
    window.addEventListener('load', () => this.medirChrome());
    window.addEventListener(
      'resize',
      typeof debounce === 'function' ? debounce(() => this.medirChrome(), 150) : () => this.medirChrome()
    );
    this.vigilarScroll();

    this.limpiarRastroDeTiendas();
    this.recibirListaDeLaUrl();
  }

  /**
   * Publica dos medidas que el CSS no puede calcular solo.
   *
   * `--deck-lista-tope` es dónde EMPIEZA de verdad la columna derecha. El CSS
   * la daba por sentada: el panel es `sticky` y su alto se calculaba como si ya
   * estuviera pegado arriba, pero sin scroll está 84px más abajo. Nadie
   * descontaba esa diferencia y el botón de comprar —el CTA primario— quedaba
   * fuera de la pantalla. Medido antes del arreglo: viewport 1285, botón
   * terminando en 1335.
   *
   * Se mide `.deck-builder__results`, que NO es sticky, así que su posición en
   * el documento es estable con o sin scroll.
   *
   * `--deck-barra-alto` es el hueco que hay que dejarle a la barra fija de
   * móvil para que no tape el último renglón de la lista.
   */
  medirChrome() {
    if (!this.container) return;

    const resultados = document.querySelector('.deck-builder__results');
    // Sin resultados la reja está en `display: none` y todo mide 0: escribir ese
    // 0 dejaría el panel del alto de la pantalla entera.
    if (resultados && resultados.offsetHeight > 0) {
      const tope = Math.round(resultados.getBoundingClientRect().top + window.scrollY);
      this.container.style.setProperty('--deck-lista-tope', `${tope}px`);
    }

    if (this.barra && this.barra.offsetHeight > 0) {
      const alto = `${Math.round(this.barra.offsetHeight)}px`;
      this.container.style.setProperty('--deck-barra-alto', alto);
      // También en la raíz: el globo de WhatsApp vive fuera de `.deck-builder`
      // y necesita saber cuánto subirse para no taparle el botón a la barra.
      document.documentElement.style.setProperty('--deck-barra-alto', alto);
    }

    // La clase le dice al resto de la página que abajo hay una barra ocupando.
    // Es el mismo mecanismo con el que el drawer del header aparta ese globo.
    document.body.classList.toggle('deck-builder-con-barra', this.resultados.length > 0);
  }

  /**
   * La barra de móvil se quita de en medio mientras te mueves y vuelve al parar.
   *
   * Ancla el precio y el botón al fondo —que es donde se alcanzan con el pulgar—
   * sin cobrarte esos 80px de lista mientras estás leyendo. Vuelve sola: no hay
   * que scrollear hacia arriba para recuperarla, que es lo que molesta del
   * patrón clásico de header que se esconde.
   *
   * Se escuchan los dos scrolls porque en móvil son dos: la página, y la lista,
   * que tiene su propio contenedor con overflow. La clase se toca dentro de
   * `requestAnimationFrame`, como hace el header del tema.
   */
  vigilarScroll() {
    if (!this.barra) return;

    let quieto;
    const alScrollear = () => {
      if (!this.barra.classList.contains('is-oculta')) {
        requestAnimationFrame(() => this.barra.classList.add('is-oculta'));
      }
      clearTimeout(quieto);
      quieto = setTimeout(() => {
        requestAnimationFrame(() => this.barra.classList.remove('is-oculta'));
      }, DeckBuilder.REAPARECE_MS);
    };

    window.addEventListener('scroll', alScrollear, { passive: true });
    if (this.listContent) this.listContent.addEventListener('scroll', alScrollear, { passive: true });
  }

  /**
   * Barre lo que dejó el buscador de otras tiendas.
   *
   * Guardaba una clave por carta y por día en `localStorage` y nunca las
   * borraba, así que en el navegador de quien usó la herramienta quedó el
   * historial de qué cartas anduvo buscando, sin caducidad. Al quitar esa
   * funcionalidad, el rastro se va con ella.
   */
  limpiarRastroDeTiendas() {
    try {
      Object.keys(localStorage)
        .filter(clave => clave.startsWith('store_search_'))
        .forEach(clave => localStorage.removeItem(clave));
    } catch (error) {
      // localStorage puede estar bloqueado (modo privado, cookies de terceros).
      // No poder limpiar no debe impedir usar el buscador.
    }
  }

  /**
   * Lee el carrito de Shopify. Nunca falla hacia arriba.
   *
   * Si no se puede leer se sigue con el mapa anterior —o vacío— y a lo sumo
   * volvemos al comportamiento viejo: el usuario ve la lista y, si algo choca,
   * se lo decimos al agregar. Que no conteste `/cart.js` no puede dejar a nadie
   * sin cotizar su mazo.
   */
  async cargarCarrito() {
    try {
      const respuesta = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!respuesta.ok) return;
      const carrito = await respuesta.json();
      if (!carrito || !Array.isArray(carrito.items)) return;

      const mapa = new Map();
      carrito.items.forEach(item => {
        const clave = String(item.variant_id);
        mapa.set(clave, (mapa.get(clave) || 0) + item.quantity);
      });
      this.enCarrito = mapa;
    } catch (error) {
      // Red caída o respuesta ilegible: se sigue con lo que había.
    }
  }

  /** Cuántas unidades de ESTA variante ya están en el carrito. */
  enCarritoDe(versionId) {
    return this.enCarrito.get(String(versionId)) || 0;
  }

  /** Lo que hay que cubrir de esta carta: lo que el usuario pidió, o lo editó. */
  cantidadDe(resultado) {
    return resultado.cantidad === undefined ? resultado.quantity : resultado.cantidad;
  }

  /**
   * El usuario ajusta cuántas quiere de una carta.
   *
   * Faltaba y era la salida natural del callejón: cuando de tres solo hay dos,
   * lo que uno quiere hacer es pedir dos y seguir, no quedarse discutiendo con
   * la pantalla. El tope es lo que de verdad se puede llevar —stock menos lo
   * que ya trae en el carrito—, o lo que decía su lista, lo que sea mayor: bajar
   * y volver a subir a lo original tiene que ser posible.
   */
  cambiarCantidad(indice, nueva) {
    const resultado = this.resultados[indice];
    if (!resultado) return;

    const tope = Math.max(resultado.topeDisponible || 0, resultado.quantity);
    const limpia = Math.min(Math.max(Math.round(nueva) || 0, 1), tope);
    if (limpia === this.cantidadDe(resultado)) {
      // Repintar igual: si el campo quedó con un valor fuera de rango hay que
      // devolverlo a lo que el estado dice, o la pantalla miente.
      this.pintar();
      return;
    }

    resultado.cantidad = limpia;
    // Sin `medir`: `/api/events` tiene lista blanca (ADR 150) y el catálogo dice
    // que no se agrega un evento que nadie va a leer. Medir si la gente edita
    // cantidades vale la pena, pero es una línea en `lib/events/catalog.ts` y su
    // lectura en el registro de métricas — o sea, un PR en CardVault, no aquí.
    //
    // Se recalcula la lista ENTERA, no solo esta línea: bajar de 4 a 1 libera
    // stock que otra línea de la misma lista puede estar necesitando.
    this.recalcularTodo();
    // Subir la cantidad de una carta que estaba apagada implica quererla.
    if (!resultado.seleccionada) resultado.seleccionada = true;
    this.pintar();
  }

  /**
   * El único camino para prender o apagar una carta. Da igual si el clic vino
   * del checkbox de la lista o del tache de la reja: los dos terminan aquí, y
   * de aquí sale un repintado que deja las tres vistas de acuerdo.
   */
  alternarSeleccion(indice, seleccionada, origen) {
    const resultado = this.resultados[indice];
    if (!resultado) return;
    resultado.seleccionada = seleccionada;
    // Solo se mide QUITAR, no volver a poner: la pregunta que importa es cuánta
    // gente corrige lo que el buscador le armó.
    if (!seleccionada) medir('deck_remove', origen || 'casilla');
    // Apagar una línea suelta el stock que reclamaba, y puede ser justo el que
    // le faltaba a otra línea de la misma lista.
    this.recalcularTodo();
    this.pintar();
  }

  /**
   * Lista que llega del hero de la landing (ADR 153): `?lista=4 Sol Ring\n...`.
   *
   * El usuario ya expresó su intención al pegarla allá, así que aquí no se le
   * pide que la vuelva a confirmar: se precarga y se busca sola. Volver a
   * pedirle un clic sería cobrarle dos veces por la misma decisión.
   */
  recibirListaDeLaUrl() {
    const lista = new URLSearchParams(window.location.search).get('lista');
    if (!lista) return;

    this.textarea.value = lista;
    // Solo esta búsqueda viene de la URL: si el usuario edita y vuelve a buscar,
    // esa ya es suya. `parseDeckList` lo lee y lo apaga.
    this.listaDeLaUrl = true;
    this.parseDeckList();
  }

  /**
   * El catálogo de la app (ADR 158). Es la fuente buena: sabe qué carta es cada
   * producto, cosa que la búsqueda de Shopify no puede saber porque solo compara
   * títulos.
   */
  static get API_CATALOGO() {
    return 'https://cards.the-vault.mx/api/catalog/card-versions';
  }

  /**
   * Cuántas búsquedas al mismo tiempo.
   *
   * Antes se disparaban TODAS de golpe: una lista de Commander son 100 líneas y
   * salían 100 peticiones simultáneas contra un navegador que abre ~6
   * conexiones por host y contra un Shopify que las estrangula. Seis es el
   * ancho real del canal; pedir más solo hace cola en otro lado, sin avisar.
   */
  static get CONCURRENCIA() {
    return 6;
  }

  /**
   * Tope de líneas. No es una restricción del negocio: es que un pegado
   * accidental de un archivo entero no se convierta en miles de peticiones. Lo
   * que sobra se dice, no se tira callado.
   */
  static get MAX_LINEAS() {
    return 200;
  }

  /**
   * Reintentos por carta cuando Shopify contesta 429.
   *
   * `/cart/add.js` está limitado por tasa y el camino uno-por-uno lo tocaba seis
   * veces en paralelo: a la tercera carta la tienda empezaba a contestar 429 y
   * este código lo leía como "se agotó". Un mazo de 40 cartas se reportaba medio
   * agotado sin que faltara una sola.
   */
  static get REINTENTOS() {
    return 2;
  }

  /** Cuánto espera la barra de móvil, tras el último scroll, para reaparecer. */
  static get REAPARECE_MS() {
    return 250;
  }

  /** Primera espera del backoff, en milisegundos. Se duplica en cada intento. */
  static get ESPERA_BASE() {
    return 700;
  }

  static esperar(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Corre `trabajo` sobre `items` con un tope de tareas en vuelo. Los obreros
   * van tomando el siguiente pendiente conforme se desocupan, así que el lento
   * de uno no detiene a los demás — que es lo que pasaría partiendo en tandas.
   */
  static async enCola(items, limite, trabajo) {
    const resultados = new Array(items.length);
    let siguiente = 0;

    const obreros = Array.from(
      { length: Math.min(limite, items.length) },
      async () => {
        while (siguiente < items.length) {
          const indice = siguiente++;
          resultados[indice] = await trabajo(items[indice], indice);
        }
      }
    );

    await Promise.all(obreros);
    return resultados;
  }

  /**
   * Encabezados de sección que traen los exports. No son cartas y no se avisan:
   * ignorarlos es lo correcto, y anunciarlos sería ruido en cada lista pegada.
   */
  static get SECCIONES() {
    return [
      'deck', 'mainboard', 'main', 'sideboard', 'commander', 'companion',
      'maybeboard', 'considering', 'tokens', 'about', 'stickers', 'attractions'
    ];
  }

  parseDeckList() {
    // De dónde vino ESTA búsqueda (ver `medirBusqueda`). Se lee y se apaga
    // aquí arriba para que ninguna salida temprana deje el `true` puesto.
    const origen = this.listaDeLaUrl ? 'url' : 'pegada';
    this.listaDeLaUrl = false;
    const lineas = this.textarea.value.split('\n');
    const cartas = [];
    const ignoradas = [];

    lineas.forEach(lineaCruda => {
      const linea = lineaCruda.trim();

      // Vacías, comentarios y encabezados de sección: fuera, sin avisar.
      if (!linea) return;
      if (linea.startsWith('//') || linea.startsWith('#')) return;
      if (DeckBuilder.SECCIONES.includes(linea.toLowerCase().replace(/:$/, ''))) return;

      // "SB: 2 Negate" — notación vieja de sideboard.
      const sinPrefijo = linea.replace(/^SB:\s*/i, '');

      // "4 Bolt", "4x Bolt", "4X Bolt" (Moxfield, Archidekt, MTGA).
      const conCantidad = sinPrefijo.match(/^(\d+)\s*x?\s+(.+)$/i);

      // Una línea sin cantidad SÍ es una carta: vale 1. Antes se descartaba en
      // silencio, así que "Sol Ring" a secas —como se escribe una lista a
      // mano— desaparecía sin que el usuario se enterara nunca.
      const cantidad = conCantidad ? parseInt(conCantidad[1], 10) : 1;
      const nombreCrudo = conCantidad ? conCantidad[2] : sinPrefijo;
      const name = this.normalizeCardName(nombreCrudo);

      // Un nombre de carta tiene letras. Sin este filtro, una línea de basura
      // como "???!!!" pasaba como carta, volvía "No disponible" y se leía como
      // "no la tenemos" en vez de "esto no se pudo leer" — que es peor que
      // tirarla, porque miente con seguridad.
      if (!name || !/[a-záéíóúüñ]/i.test(name)) {
        ignoradas.push(linea);
        return;
      }

      // El set y el número de coleccionista se quitan del nombre para buscar,
      // pero no se tiran: si la carta falta y se pide por Bounty, al operador
      // le sirve saber qué impresión traía la lista.
      const impresion = String(nombreCrudo).match(/\(([A-Za-z0-9]{2,6})\)\s*([0-9][0-9A-Za-z★-]*)?/);

      cartas.push({
        quantity: cantidad,
        name,
        set: impresion ? impresion[1].toUpperCase() : null,
        numero: impresion && impresion[2] ? impresion[2] : null
      });
    });

    // Lo que no se pudo leer se dice. Que el sistema tire líneas sin avisar es
    // peor que no leerlas: el usuario cree que cotizó su lista completa.
    this.lineasIgnoradas = ignoradas;

    if (cartas.length > DeckBuilder.MAX_LINEAS) {
      const sobran = cartas.length - DeckBuilder.MAX_LINEAS;
      cartas.length = DeckBuilder.MAX_LINEAS;
      this.lineasIgnoradas = ignoradas.concat(
        `…y ${sobran} ${sobran === 1 ? 'carta más que no cabe' : 'cartas más que no caben'} en una sola búsqueda (el tope son ${DeckBuilder.MAX_LINEAS}).`
      );
    }

    if (cartas.length === 0) {
      this.container.classList.remove('is-searching');
      this.resultados = [];
      this.pintar();
      return;
    }

    // `deck_search` ya no se mide aquí sino al terminar la búsqueda: antes de
    // buscar no se sabe qué falta, y "qué falta" es justo lo que el proyecto
    // Bounty necesita medir. Ver `medirBusqueda`.
    this.searchCards(cartas, origen);
  }

  /**
   * Limpia el nombre antes de buscarlo. Los exports traen el set y el número de
   * coleccionista pegados ("Sol Ring (LTC) 280"), y la búsqueda de Shopify se
   * degrada o devuelve cero con ese ruido.
   *
   * EL ORDEN DE LAS REGLAS IMPORTA, y antes estaba al revés: la regla del número
   * suelto exige que el número vaya al final, pero en "Arcane Signet 242 *F*" al
   * final está el marcador de foil, así que no limpiaba nada y se terminaba
   * buscando "Arcane Signet 242". Los marcadores y las categorías se quitan
   * primero, para que el número quede expuesto al final cuando le toque.
   */
  normalizeCardName(raw) {
    return String(raw || '')
      // marcadores de foil/etched tipo *F* *E*, en cualquier posición
      .replace(/\s*\*[A-Za-z]\*/g, '')
      // categoría de Archidekt al final: "Sol Ring (LTC) 280 [Ramp]"
      .replace(/\s*\[[^\]]*\]\s*$/, '')
      // set + número de coleccionista: "(LTC) 280", "(CLB) 187"
      .replace(/\s*\([A-Za-z0-9]{2,6}\)\s*[\d★]*.*$/, '')
      // número de coleccionista suelto al final: "Sol Ring 280"
      .replace(/\s+#?\d+$/, '')
      .trim();
  }

  /**
   * Trae las versiones con stock de UN nombre. Una sola petición por nombre
   * distinto: una lista de Commander repite básicos y cartas entre mazo y
   * sideboard, y antes cada repetición era su propia búsqueda.
   *
   * Pregunta primero al catálogo (ADR 158) y solo si eso falla cae a la
   * búsqueda de Shopify. La diferencia entre los dos no es de velocidad: el
   * catálogo SABE qué carta es cada producto, la búsqueda de Shopify solo compara
   * texto de títulos y por eso cubría "4 Lightning Bolt" con un Transcantation.
   */
  async obtenerVersiones(nombre) {
    if (this.cacheBusquedas.has(nombre)) return this.cacheBusquedas.get(nombre);

    const promesa = (async () => {
      const delCatalogo = await this.versionesDelCatalogo(nombre);
      if (delCatalogo) return delCatalogo;
      return this.versionesDeShopify(nombre);
    })();

    this.cacheBusquedas.set(nombre, promesa);
    return promesa;
  }

  /**
   * El catálogo de la app. Devuelve `null` —no vacío— si no se pudo preguntar,
   * para que el llamador sepa que debe caer al otro camino. Vacío significaría
   * "no hay", que es una respuesta distinta.
   */
  async versionesDelCatalogo(nombre) {
    let datos;

    try {
      const respuesta = await fetch(
        `${DeckBuilder.API_CATALOGO}?name=${encodeURIComponent(nombre)}`,
        { credentials: 'omit' }
      );
      if (!respuesta.ok) return null;
      datos = await respuesta.json();
    } catch (error) {
      // Red caída, CORS, el endpoint todavía sin desplegar: se cae al otro
      // camino en silencio. Que el catálogo no conteste no debe dejar al
      // comprador sin cotizar su lista.
      return null;
    }

    if (!datos || !Array.isArray(datos.versions)) return null;

    // Solo la carta pedida. Es el arreglo del punto #2: la cobertura deja de
    // completarse con productos que solo se PARECEN en el título.
    const mismas = datos.versions.filter(version => version.is_same_card);
    if (mismas.length === 0) return { versiones: [], productoBase: null };

    const versiones = mismas.map(version => ({
      // El catálogo guarda el variant como GID (`gid://shopify/ProductVariant/123`)
      // y a veces como número suelto, según de dónde entró la carta. `/cart/add.js`
      // solo entiende el número: mandarle el GID falla la compra entera.
      id: DeckBuilder.idNumerico(version.shopify_variant_id),
      // El deck builder venía hablando el idioma de Shopify, donde el título de
      // la variante trae condición e idioma pegados y el precio va en centavos.
      // El catálogo los da por separado y en pesos: se traducen aquí, en la
      // frontera, para que el resto del archivo no tenga que saber de dónde
      // vino cada cosa.
      title: DeckBuilder.tituloDeVariante(version),
      price: Math.round(Number(version.price || 0) * 100),
      inventory_quantity: version.quantity,
      available: true,
      product: {
        id: version.shopify_product_id,
        title: version.name,
        featured_image: { url: version.image_url }
      }
    }));

    // El catálogo devuelve sus versiones ordenadas por PRECIO ascendente, y eso
    // no es el orden en que se ofrecen. Se reordena aquí, en la frontera, con la
    // misma regla que aplica el camino de Shopify: mientras esto no se hizo, el
    // modo "Mejor condición" entregaba la más barata —un Rhystic Study Lightly
    // Played de $1,276 por encima del Near Mint de $1,501— y el interruptor
    // "Precio más bajo" quedaba inerte, porque ya estaba en lo más barato
    // (VAU-231).
    versiones.sort(DeckBuilder.compararPorCondicion);

    return { versiones, productoBase: versiones[0].product };
  }

  /** `gid://shopify/ProductVariant/123` → `123`. Lo que entiende el carrito. */
  static idNumerico(id) {
    return String(id || '').replace(/^gid:\/\/shopify\/(ProductVariant|Product)\//, '');
  }

  /** "Near Mint", "Near Mint Foil", "Near Mint (Español)" — como lo espera la UI. */
  static tituloDeVariante(version) {
    const partes = [version.condition || 'Sin condición'];
    if (version.foil) partes.push('Foil');
    if (version.language && version.language.toLowerCase() !== 'en') {
      partes.push(version.language.toLowerCase() === 'es' ? '(Español)' : `(${version.language})`);
    }
    return partes.join(' ');
  }

  /**
   * Las condiciones, de la mejor a la peor. Es el orden de la industria, no una
   * preferencia nuestra.
   */
  static get CONDICIONES() {
    return ['near mint', 'lightly played', 'moderately played', 'heavily played', 'damaged'];
  }

  /**
   * El orden en que se OFRECE una versión cuando el usuario no pidió "lo más
   * barato": condición, luego no-foil, luego inglés, y a igualdad de todo eso el
   * precio más bajo.
   *
   * Está aquí, compartido, porque los dos caminos que traen versiones —el
   * catálogo y la búsqueda de Shopify— tienen que dar el MISMO orden para la
   * misma carta. Cuando cada uno ordenaba por su cuenta, uno de los dos no
   * ordenaba: el catálogo entregaba lo suyo como venía de la API, por precio
   * ascendente, y "Mejor condición" elegía la más barata (VAU-231).
   *
   * Lee del `title` porque es el único campo que los dos caminos comparten: el
   * catálogo lo arma en `tituloDeVariante` con el mismo vocabulario que trae la
   * variante de Shopify ("Near Mint", "Near Mint Foil", "Near Mint (Español)").
   *
   * El desempate por precio es lo que el camino de Shopify no tenía y sí
   * necesita: de una carta muy reimpresa hay decenas de Near Mint en inglés a
   * precios distintos, y sin desempate cuál queda primero depende del orden en
   * que llegaron.
   */
  static compararPorCondicion(a, b) {
    const tituloA = String(a.title || '').toLowerCase();
    const tituloB = String(b.title || '').toLowerCase();

    // Una condición que no reconocemos va al FINAL, no al principio. `findIndex`
    // devuelve -1 cuando no encuentra, y dejarlo tal cual pondría un "Sin
    // condición" por delante del Near Mint.
    const rango = titulo => {
      const indice = DeckBuilder.CONDICIONES.findIndex(condicion => titulo.includes(condicion));
      return indice === -1 ? DeckBuilder.CONDICIONES.length : indice;
    };

    const condicion = rango(tituloA) - rango(tituloB);
    if (condicion !== 0) return condicion;

    const aFoil = tituloA.includes('foil');
    const bFoil = tituloB.includes('foil');
    if (aFoil !== bFoil) return aFoil ? 1 : -1;

    const aEspanol = tituloA.includes('español');
    const bEspanol = tituloB.includes('español');
    if (aEspanol !== bEspanol) return aEspanol ? 1 : -1;

    return (Number(a.price) || 0) - (Number(b.price) || 0);
  }

  /**
   * El camino viejo: la búsqueda de Shopify. Se conserva como red de seguridad,
   * no como plan A — no distingue "otra impresión de esta carta" de "otra carta
   * que se le parece", y ese es justamente el bug que el catálogo resuelve.
   */
  async versionesDeShopify(nombre) {
    let respuesta;
    try {
      respuesta = await fetch(`/search?view=deck-builder&q=${encodeURIComponent(nombre)}&type=product`);
    } catch (errorDeRed) {
      console.error(`Error de red buscando ${nombre}:`, errorDeRed);
      return null;
    }

    if (!respuesta.ok) {
      console.error(`Error en búsqueda de ${nombre}:`, respuesta.status);
      return null;
    }

    let datos;
    try {
      datos = JSON.parse(await respuesta.text());
    } catch (errorDeFormato) {
      console.error(`Respuesta ilegible para ${nombre}:`, errorDeFormato);
      return null;
    }

    if (!datos.results || datos.results.length === 0) return null;

    const versiones = [];

    for (const producto of datos.results) {
      const conStock = producto.variants.filter(
        variante => variante.available && variante.inventory_quantity > 0
      );

      conStock.sort(DeckBuilder.compararPorCondicion);

      for (const variante of conStock) {
        versiones.push({ ...variante, product: producto });
      }
    }

    return { versiones, productoBase: datos.results[0] };
  }

  async searchCards(cards, origen) {
    this.container.classList.add('is-searching');
    this.container.classList.remove('has-results');
    this.progressBar.style.width = '0%';
    this.cacheBusquedas = new Map();
    this.ultimoEnvio = null;

    // El carrito se lee ANTES de repartir, no después de que Shopify rechace:
    // la disponibilidad que se pinta tiene que ser la que el carrito va a
    // aceptar. Va en paralelo con las búsquedas — es una petición, no una etapa.
    // El `catch` es a propósito y va aquí, no solo dentro: esta promesa se
    // espera DENTRO del `try` de la búsqueda, así que un rechazo suyo se
    // llevaría por delante la lista entera y pintaría "error al buscar las
    // cartas". Saber qué hay en el carrito es una mejora, nunca un requisito.
    const carritoListo = this.cargarCarrito().catch(() => {});

    try {
      let terminadas = 0;

      const buscarUna = async card => {
        const hallazgo = await this.obtenerVersiones(card.name);
        terminadas++;
        this.progressBar.style.width = `${(terminadas / cards.length) * 100}%`;

        if (!hallazgo) {
          return { ...card, found: false, versiones: [], versionPreferidaId: null, versionesAbiertas: false, productoBase: null };
        }

        return {
          ...card,
          found: hallazgo.versiones.length > 0,
          versiones: hallazgo.versiones,
          // Null = "la que eligió el sistema". En cuanto el usuario toca el
          // selector, aquí queda su decisión y ya no se le pisa.
          versionPreferidaId: null,
          versionesAbiertas: false,
          productoBase: hallazgo.productoBase
        };
      };

      const results = await DeckBuilder.enCola(cards, DeckBuilder.CONCURRENCIA, buscarUna);
      await carritoListo;

      this.progressBar.style.width = '100%';

      await new Promise(resolve => setTimeout(resolve, 200));

      this.container.classList.remove('is-searching');
      this.container.classList.add('has-results');
      this.updateUI(results);
      this.medirBusqueda(cards, origen);
    } catch (error) {
      console.error('Error buscando cartas:', error);
      // La búsqueda se cuenta aunque se caiga: es el denominador del embudo.
      // Sin resultados no se sabe qué faltó, así que solo van las pedidas.
      medir('deck_search', cards.length, { pedidas: DeckBuilder.copiasPedidas(cards), origen: origen || 'pegada' });
      this.container.classList.remove('is-searching');
      this.cardsGrid.innerHTML = '<div class="error-message">Error al buscar las cartas. Por favor, intenta de nuevo.</div>';
    }
  }

  /**
   * El denominador de todo: cuántas listas se buscan, y desde el proyecto Bounty
   * (ADR 236) también qué les faltó.
   *
   * `target` sigue siendo el número de LÍNEAS, como siempre, para no romper la
   * serie histórica. La metadata habla en COPIAS —las mismas que dice la
   * pantalla en "Faltan X de Y cartas"— y lleva los nombres de lo que no
   * tenemos, recortados para que el JSON quepa en el tope del endpoint.
   *
   * `origen` separa la lista que llegó por `?lista=` (la landing o un enlace
   * compartido) de la que se pegó aquí: el hero manda `landing_list_cotizar`
   * antes de saber qué falta, y este evento es el que lo completa.
   */
  medirBusqueda(cards, origen) {
    const pedidas = DeckBuilder.copiasPedidas(cards);
    const conFalta = this.resultados.filter(resultado => this.faltaDe(resultado) > 0);
    const faltantes = conFalta.reduce((suma, resultado) => suma + this.faltaDe(resultado), 0);
    const nombres = conFalta.map(resultado => resultado.name);

    const metadata = typeof window.vaultMetadataFaltantes === 'function'
      ? window.vaultMetadataFaltantes(pedidas, faltantes, nombres, { origen: origen || 'pegada' })
      : { pedidas, faltantes, origen: origen || 'pegada' };

    medir('deck_search', cards.length, metadata);
  }

  static copiasPedidas(cards) {
    return cards.reduce((suma, card) => suma + (Number(card.quantity) || 0), 0);
  }

  /**
   * Cuántas copias de esta línea NO se pueden llevar: ni las tenemos libres ni
   * ya están en tu carrito. Es la misma cuenta que el "Faltan X" del resumen,
   * línea por línea. Una carta desmarcada a propósito no falta: su
   * `availableQuantity` se calcula igual aunque no se lleve.
   */
  faltaDe(resultado) {
    // Desmarcada no falta: ya la tiene o no la quiere, y no se pide al Bounty.
    if (resultado.seleccionada === false) return 0;
    // Las copias de la otra versión que sí tenemos ("Agregar esta") ya no faltan.
    const deLaOtra = resultado.cambiadaPor ? resultado.cambiadaPor.copias : 0;
    const quiere = this.cantidadDe(resultado);
    const cubiertas = resultado.disponible ? (resultado.availableQuantity || 0) : 0;
    const enCarrito = Math.min(resultado.enCarrito || 0, quiere);
    return Math.max(0, quiere - cubiertas - enCarrito - deLaOtra);
  }

  /**
   * Convierte el resultado crudo de la búsqueda en el estado de la pantalla.
   * De aquí en adelante nadie vuelve a mirar `results`: se mira
   * `this.resultados`.
   */
  updateUI(results) {
    // La cobertura se calcula ANTES de ordenar: `disponible` sale de repartir la
    // cantidad entre las versiones con stock, así que hasta que no se reparte no
    // se sabe quién va primero.
    const conEstado = results.map(resultado => ({
      ...resultado,
      versiones: resultado.versiones || [],
      // `quantity` es lo que decía la lista y no se toca: es el denominador de
      // "2 de 3". `cantidad` es lo que el usuario quiere ahora, y es lo que se
      // reparte.
      cantidad: resultado.quantity
    }));

    // El reparto necesita ver la lista completa —una carta puede venir en dos
    // líneas— así que se publica el estado antes de calcular, no dentro del map.
    this.resultados = conEstado;
    this.recalcularTodo();

    // Todo entra marcado: quien pega una lista quiere comprarla —lo que hay hoy
    // y lo que falta por Bounty—, no volver a elegirla carta por carta.
    conEstado.forEach(item => { item.seleccionada = true; });

    // Las disponibles primero. Dentro de cada grupo se respeta el orden en que
    // venían escritas, que es el que el usuario reconoce de su propia lista.
    this.resultados = conEstado.sort((a, b) => {
      if (a.disponible === b.disponible) return 0;
      return a.disponible ? -1 : 1;
    });

    this.pintar();
  }

  /**
   * Reparte la cantidad pedida entre las versiones que tienen stock.
   *
   * Si el usuario eligió una, esa va primero y el resto se completa con las
   * demás en el orden de siempre: decir "quiero esta" no debería costarle
   * quedarse corto cuando esa versión no alcanza para toda la cantidad.
   */
  calcularCobertura(resultado, reclamado) {
    const candidatas = this.aplicarFiltros(resultado.versiones);

    // Si los filtros dejaron la carta sin nada, se dice — no se cae de la lista
    // como si no la tuviéramos. No es lo mismo "no hay" que "no hay como la
    // pediste".
    resultado.ocultasPorFiltro = resultado.versiones.length - candidatas.length;

    // `id` viaja como número en el JSON y como cadena cuando vuelve de un
    // `data-` del DOM: se comparan como texto para que no se pierdan entre sí.
    const preferida = resultado.versionPreferidaId === null
      ? undefined
      : candidatas.find(
          version => String(version.id) === String(resultado.versionPreferidaId)
        );

    const ordenadas = this.ordenarVersiones(candidatas);
    const orden = preferida
      ? [preferida, ...ordenadas.filter(version => version !== preferida)]
      : ordenadas;

    // Lo que queda LIBRE de cada versión: su stock, menos lo que el usuario ya
    // metió al carrito, menos lo que otras líneas de esta misma lista ya
    // reclamaron. Pedirle a Shopify más que esto es pedirle un 422.
    const yaReclamado = version => (reclamado ? reclamado.get(String(version.id)) || 0 : 0);
    const libreDe = version =>
      Math.max(0, version.inventory_quantity - this.enCarritoDe(version.id) - yaReclamado(version));

    let porCubrir = this.cantidadDe(resultado);
    const elegidas = [];

    for (const version of orden) {
      if (porCubrir === 0) break;
      const tomar = Math.min(porCubrir, libreDe(version));
      if (tomar > 0) {
        elegidas.push({ ...version, quantityToAdd: tomar });
        porCubrir -= tomar;
        if (reclamado) reclamado.set(String(version.id), yaReclamado(version) + tomar);
      }
    }

    resultado.candidatas = ordenadas;
    resultado.selectedVariants = elegidas;
    resultado.availableQuantity = this.cantidadDe(resultado) - porCubrir;
    resultado.disponible = elegidas.length > 0;
    // Cuánto de esta carta ya trae en el carrito y cuánto más podría llevarse.
    // Los dos hacen falta para no volver a decirle "no disponible" a una carta
    // que sí tiene, solo que ya es suya.
    resultado.enCarrito = candidatas.reduce((suma, version) => suma + this.enCarritoDe(version.id), 0);
    resultado.topeDisponible = candidatas.reduce((suma, version) => suma + libreDe(version), 0);
    resultado.product = elegidas.length > 0
      ? {
          ...elegidas[0].product,
          available: true,
          price: elegidas[0].price,
          featured_image: elegidas[0].product.featured_image
        }
      : { ...(resultado.productoBase || {}), available: false };
  }

  /**
   * Reparte el stock entre TODAS las líneas, en el orden en que están escritas.
   *
   * Antes cada línea se calculaba sola, contra el stock completo, y una lista
   * real rompe eso enseguida: "2 Duress" en el mazo y "2 Duress" en el sideboard
   * son dos líneas distintas, las dos decían 2/2, y entre las dos le pedían 4 a
   * una bóveda que tenía 2. El carrito lo rechazaba y el usuario leía "ya no
   * está disponible" sobre una carta que sí teníamos — el mismo error que este
   * arreglo persigue, pero contra la propia lista en vez de contra el carrito.
   *
   * Una línea apagada no reclama nada: si quitas el Duress del mazo, el del
   * sideboard se vuelve a poder llevar.
   */
  recalcularTodo() {
    const reclamado = new Map();
    this.resultados.forEach(resultado => {
      this.calcularCobertura(resultado, resultado.seleccionada === false ? null : reclamado);
    });
  }

  /**
   * Los filtros son exclusiones de verdad, no preferencias suaves. Quien marca
   * "solo inglés" no quiere la española en tercer lugar: no la quiere. Si eso
   * deja la carta sin nada, se le dice — ver `ocultasPorFiltro`.
   */
  aplicarFiltros(versiones) {
    return versiones.filter(version => {
      const titulo = version.title.toLowerCase();
      if (this.preferencias.soloIngles && titulo.includes('español')) return false;
      if (this.preferencias.sinFoil && titulo.includes('foil')) return false;
      return true;
    });
  }

  /**
   * El orden con el que se elige sola la versión.
   *
   * `condicion` no reordena nada AQUÍ, y eso ya no significa "el orden en que
   * llegaron": los dos caminos que traen versiones las ordenan en la frontera
   * con `compararPorCondicion` —condición, no-foil, inglés, y precio como
   * desempate—, así que la lista ya viene en el orden bueno. Lo que se respeta
   * al no tocarla es el agrupado por producto del camino de Shopify: su búsqueda
   * difusa mete cartas ajenas, y reordenar la lista completa dejaría que un Near
   * Mint de otro producto le ganara a un Lightly Played del correcto.
   *
   * `precio` sí reordena la lista completa, y ahí ese riesgo no aplica: el
   * usuario está pidiendo explícitamente lo más barato, no dejando que el
   * sistema adivine por él.
   */
  ordenarVersiones(versiones) {
    if (this.preferencias.orden !== 'precio') return versiones;
    return [...versiones].sort((a, b) => a.price - b.price);
  }

  /**
   * Cuánto costaría la lista con cada criterio, para que elegir no sea a
   * ciegas: "Mejor condición · $30,541" contra "Más barato · $24,180".
   * Se simula con el mismo motor (recalcularTodo) y se regresa al criterio
   * elegido; las elecciones a mano del usuario se respetan en los dos.
   */
  pintarTotalesPorCriterio() {
    const nodos = document.querySelectorAll('[data-total-criterio]');
    if (!nodos.length || this.resultados.length === 0) return;

    const original = this.preferencias.orden;
    const totales = {};
    ['condicion', 'precio'].forEach(orden => {
      this.preferencias.orden = orden;
      this.recalcularTodo();
      totales[orden] = { monto: this.montoDisponible() / 100, cartas: this.unidadesDisponibles() };
    });
    this.preferencias.orden = original;
    this.recalcularTodo();

    const pesos = valor => (window.VaultBounty
      ? window.VaultBounty.pesos(valor)
      : '$' + Math.round(valor).toLocaleString('es-MX'));
    const ahorro = totales.condicion.monto - totales.precio.monto;

    nodos.forEach(nodo => {
      const total = totales[nodo.dataset.totalCriterio];
      if (!total) return;
      let texto = `${pesos(total.monto)} · ${total.cartas} ${total.cartas === 1 ? 'carta' : 'cartas'}`;
      nodo.textContent = texto;
      const caja = nodo.closest('.deck-builder__opcion-caja');
      const previo = caja && caja.querySelector('.deck-builder__opcion-ahorro');
      if (previo) previo.remove();
      if (caja && nodo.dataset.totalCriterio === 'precio' && ahorro >= 1) {
        const chip = document.createElement('span');
        chip.className = 'deck-builder__opcion-ahorro';
        chip.textContent = `Ahorras ${pesos(ahorro)}`;
        caja.appendChild(chip);
      }
    });
  }

  /** El usuario eligió otra versión: se recalcula la cobertura y se repinta. */
  elegirVersion(indice, versionId) {
    const resultado = this.resultados[indice];
    if (!resultado) return;
    resultado.versionPreferidaId = versionId;
    medir('deck_version_change');
    this.recalcularTodo();
    // Elegir una versión implica quererla: si la carta estaba desmarcada, se
    // vuelve a marcar en vez de obligar a un segundo clic.
    resultado.seleccionada = true;
    this.pintar();
  }

  /**
   * El único que escribe en el DOM de resultados. Se llama entero en cada
   * cambio: repintar tres listas de decenas de elementos es barato, y a cambio
   * desaparece la clase de bug que tenía esta pantalla —una vista actualizada y
   * las otras dos no—.
   */
  pintar() {
    // Repintar destruye el nodo que el usuario acaba de tocar, y con él su foco:
    // quien va con teclado marcaba una casilla y el foco se le caía al body,
    // perdiendo el lugar en la lista. Se anota quién tenía el foco y se le
    // devuelve al equivalente en el DOM nuevo.
    const activo = document.activeElement;
    const foco = activo && activo.dataset ? activo.dataset.foco : null;
    const indiceActivo = activo && activo.dataset ? activo.dataset.indice : null;

    this.pintarTotalesPorCriterio();
    this.pintarLista();
    this.pintarReja();
    this.pintarResumen();
    this.pintarAvisos();
    this.medirChrome();
    this.programarCotizacionBounty();

    if (!foco) return;
    // Si el control desapareció —el tache de una carta que acabas de quitar, o
    // una versión que ya no se ofrece— el foco cae en la casilla de esa misma
    // carta, que es el control equivalente visto desde la otra vista.
    const destino = document.querySelector(`[data-foco="${foco}"]`)
      || (indiceActivo !== null
        ? document.querySelector(`.deck-builder__list-content input[data-foco="check-${indiceActivo}"]`)
        : null);
    if (destino) destino.focus();
  }

  pintarLista() {
    this.listContent.innerHTML = this.resultados.map((resultado, indice) => {
      const entradaBounty = this.faltaDe(resultado) > 0 ? this.entradaBounty(resultado.name) : null;
      // Una faltante que nunca encontramos no trae foto del catálogo; la
      // cotización del Bounty sí puede traerla. Una carta se ve, siempre.
      const imagen = (resultado.product && resultado.product.featured_image && resultado.product.featured_image.url)
        || (entradaBounty && entradaBounty.imagen)
        || null;
      const variantes = resultado.selectedVariants || [];

      return `
        <div class="deck-builder__list-item">
          <div class="deck-builder__list-thumbnail ${DeckBuilder.esFaltante(resultado) ? 'not-available' : ''}">
            ${imagen
              ? `<img src="${escaparHtml(imagen)}" alt="${escaparHtml(resultado.product.title)}">`
              : '<div class="deck-builder__list-thumbnail-placeholder"></div>'}
          </div>
          <div class="deck-builder__list-details">
            ${resultado.cambiadaPor
              ? `<span class="vb-chip vb-chip--success vault-bounty-otra">✓ En tu carrito · ${resultado.cambiadaPor.copias}× ${escaparHtml(resultado.cambiadaPor.titulo)}</span>`
              : entradaBounty && window.VaultBounty
                ? window.VaultBounty.htmlOtraVersion(entradaBounty.otra_version, ` data-otra-indice="${indice}"`)
                : ''}
            ${resultado.errorOtra ? `<span class="not-found-label">${escaparHtml(resultado.errorOtra)}</span>` : ''}
            <label class="deck-builder__checkbox">
              <input
                type="checkbox"
                data-indice="${indice}"
                data-foco="check-${indice}"
                ${resultado.seleccionada ? 'checked' : ''}
                aria-label="${resultado.disponible ? 'Llevar' : 'Pedir en el Bounty'} ${escaparHtml(resultado.name)}"
              >
              <span class="deck-builder__card-count">
                ${resultado.availableQuantity || 0}/${resultado.quantity}x
                ${variantes.map(v =>
                  `<span class="deck-builder__variant-count" data-tooltip="${escaparHtml(v.title)} - $${(v.price / 100).toFixed(2)}">(${v.quantityToAdd})</span>`
                ).join(' ')}
              </span>
              <span class="name ${DeckBuilder.esFaltante(resultado) ? 'not-found' : ''}">${escaparHtml(resultado.name)}</span>
              ${this.etiquetaDeEstado(resultado)}
            </label>
            ${this.faltaDe(resultado) > 0 && window.VaultBounty
              ? `<button type="button" class="deck-builder__bounty-carta" data-bounty-carta="${indice}">🎯 Pide solo esta con un Bounty</button>`
              : ''}
            ${this.pintarCantidad(resultado, indice)}
            ${this.pintarVersiones(resultado, indice)}
          </div>
        </div>
      `;
    }).join('');
  }

  /* ------------------------------------------------------------------ *
   * Bounty (ADR 236 de CardVault)
   * ------------------------------------------------------------------ */

  /**
   * Lo que falta, como lo pide el API del Bounty: una entrada por carta (el
   * mismo nombre en mazo y sideboard se suma), con la cantidad que de verdad
   * no se puede llevar.
   *
   * El producto y la foto solo viajan cuando la línea tiene versiones, o sea
   * cuando sabemos que ES esa carta. Sin versiones, el `productoBase` del
   * camino de Shopify puede ser cualquier carta que se le parezca en el
   * título, y mandárselo al operador lo mandaría a buscar otra.
   */
  cartasParaBounty() {
    const porClave = new Map();

    this.resultados.forEach(resultado => {
      const falta = this.faltaDe(resultado);
      if (falta <= 0) return;

      const clave = `${resultado.name.toLowerCase()}|${resultado.set || ''}|${resultado.numero || ''}`;
      const previa = porClave.get(clave);
      if (previa) {
        previa.cantidad += falta;
        return;
      }

      porClave.set(clave, this.cartaParaBounty(resultado, falta));
    });

    return Array.from(porClave.values());
  }

  /** Una línea faltante como la pide el API (ver `cartasParaBounty`). */
  cartaParaBounty(resultado, cantidad) {
    const confirmada = (resultado.versiones || []).length > 0 && resultado.productoBase;
    const base = confirmada ? resultado.productoBase : null;
    return {
      nombre: resultado.name,
      cantidad,
      set: resultado.set || null,
      numero: resultado.numero || null,
      shopify_product_id: base && base.id ? DeckBuilder.idNumerico(base.id) : null,
      imagen: base && base.featured_image ? base.featured_image.url || null : null
    };
  }

  /**
   * "Pide solo esta": el mismo modal, con una sola carta y sin las disponibles
   * (no es completar el mazo: es conseguir esa). La cotización de la lista ya
   * trae su precio; el modal toma la de esa carta.
   */
  abrirBountyDeCarta(indice) {
    const resultado = this.resultados[indice];
    if (!window.VaultBounty || !resultado) return;
    const falta = this.faltaDe(resultado);
    if (falta <= 0) return;
    window.VaultBounty.abrir({
      origen: 'lista',
      cartas: [this.cartaParaBounty(resultado, falta)],
      cotizacion: this.bounty.cotizacion
    });
  }

  /**
   * "Tenemos otra versión · Agregar esta": la versión que sí tenemos va directo
   * al carrito con las copias que faltaban, y la carta deja de faltar. Antes era
   * un enlace a la ficha: sacaba al comprador de su lista a media compra.
   */
  async agregarOtraVersion(indice) {
    const resultado = this.resultados[indice];
    if (!resultado || !window.VaultBounty) return;
    const entrada = this.entradaBounty(resultado.name);
    const otra = entrada && entrada.otra_version;
    const variante = otra && window.VaultBounty.varianteDeOtra(otra);
    const copias = this.faltaDe(resultado);
    if (!variante || copias <= 0) return;

    resultado.errorOtra = null;
    // Shopify, si no alcanza el stock, agrega las que hay y responde 422: se
    // cuenta lo que de verdad entró comparando el carrito antes y después.
    await this.cargarCarrito();
    const antes = this.enCarritoDe(variante);
    let fallaDeRed = false;
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variante), quantity: copias }] })
      });
    } catch (error) {
      fallaDeRed = true;
    }
    await this.cargarCarrito();
    const entraron = Math.max(0, this.enCarritoDe(variante) - antes);

    if (entraron === 0) {
      resultado.errorOtra = fallaDeRed ? 'No pudimos agregarla, revisa tu conexión' : 'Esa versión ya no tiene copias';
      this.pintar();
      return;
    }
    resultado.cambiadaPor = {
      variante,
      titulo: otra.titulo,
      copias: (resultado.cambiadaPor ? resultado.cambiadaPor.copias : 0) + entraron
    };
    if (entraron < copias) {
      const resto = copias - entraron;
      resultado.errorOtra = `Solo había ${entraron}; ${resto === 1 ? 'la otra la buscamos' : `las otras ${resto} las buscamos`} en tu Bounty`;
    }
    this.pintar();
  }

  /** Lo que `cotizar` dijo de esta carta, si ya contestó. */
  entradaBounty(nombre) {
    const cotizacion = this.bounty.cotizacion;
    if (!cotizacion || !Array.isArray(cotizacion.cartas)) return null;
    const buscado = String(nombre || '').toLowerCase();
    return cotizacion.cartas.find(carta => String(carta.nombre || '').toLowerCase() === buscado) || null;
  }

  /** Unidades que van al carrito si el usuario da clic ahora. */
  unidadesDisponibles() {
    return this.resultados
      .filter(resultado => resultado.disponible && resultado.seleccionada)
      .reduce((suma, resultado) => suma + (resultado.availableQuantity || 0), 0);
  }

  /** Lo que cuestan, en centavos, las unidades que irían al carrito. */
  montoDisponible() {
    return this.resultados
      .filter(resultado => resultado.disponible && resultado.seleccionada)
      .reduce((suma, resultado) => suma + (resultado.selectedVariants || [])
        .reduce((parcial, variante) => parcial + variante.price * variante.quantityToAdd, 0), 0);
  }

  /**
   * Las dos salidas de la lista. Con faltantes, una card arriba del botón de
   * carrito: lo que hay (chip success + monto de hoy) y lo que falta (chip
   * pending + estimado), con UN CTA primary — "Completar mi mazo" si hay algo
   * disponible, "Pon un Bounty" si no —. El botón de carrito de siempre queda
   * abajo como secundario ("Solo agregar las N disponibles").
   */
  pintarBloqueBounty() {
    if (!window.VaultBounty) return '';
    const cartas = this.cartasParaBounty();
    if (cartas.length === 0) return '';

    // Las fotos de la cotización llenan las miniaturas de lo que el catálogo
    // no conocía.
    const conFoto = cartas.map(carta => {
      const entrada = this.entradaBounty(carta.nombre);
      return { ...carta, imagen: carta.imagen || (entrada && entrada.imagen) || null };
    });
    return window.VaultBounty.htmlSalidas({
      cartas: conFoto,
      disponibles: this.unidadesDisponibles(),
      montoHoy: this.montoDisponible() / 100,
      cotizacion: this.bounty.cotizacion
    });
  }

  /**
   * Pide la cotización de lo que falta, una vez por cada conjunto distinto de
   * faltantes. Con ella el bloque enseña las fotos y el aviso de "tenemos otra
   * versión", y el modal abre ya con precios. Si el API no contesta, todo
   * sigue: el modal lo vuelve a intentar al abrir.
   */
  programarCotizacionBounty() {
    if (!window.VaultBounty) return;

    const cartas = this.cartasParaBounty().slice(0, window.VaultBounty.MAX_CARTAS);
    const clave = cartas.length
      ? JSON.stringify(cartas.map(carta => [carta.nombre, carta.cantidad, carta.set, carta.numero]))
      : null;
    if (clave === this.bounty.clave) return;

    this.bounty.clave = clave;
    this.bounty.cotizacion = null;
    clearTimeout(this.bounty.temporizador);
    if (!clave) return;

    // Espera a que el usuario deje de mover cantidades: cada cambio de la
    // lista cambia los faltantes, y no vale una cotización por clic.
    this.bounty.temporizador = setTimeout(() => {
      const cuerpo = window.VaultBounty.cuerpoDeSolicitud('lista', 'normal', cartas);
      window.VaultBounty.cotizar(cuerpo).then(respuesta => {
        if (this.bounty.clave !== clave || !respuesta.ok || !respuesta.datos) return;
        this.bounty.cotizacion = respuesta.datos;
        this.pintar();
      });
    }, 400);
  }

  /**
   * "Completar mi mazo": UN solo pago con las disponibles + el apartado del
   * Bounty (decisión del owner, 8-oct-2026). El modal manda las variantes
   * elegidas a `/api/bounties/checkout` —solo id y cantidad; los montos los
   * pone el servidor— y redirige al link de pago. Lo que no se consiga, o lo
   * que salga más barato, regresa al Saldo. "Solo agregar las N disponibles"
   * sigue siendo el carrito de siempre (`addToCart`).
   */
  completarMazo() {
    if (!window.VaultBounty) return;
    window.VaultBounty.abrir({
      origen: 'lista',
      cartas: this.cartasParaBounty(),
      cotizacion: this.bounty.cotizacion,
      carrito: this.carritoParaPago(),
      montoHoy: this.montoDisponible() / 100,
      rebuscar: () => this.parseDeckList()
    });
  }

  /** Las variantes seleccionadas como las pide `/checkout`: id numérico y cantidad. */
  carritoParaPago() {
    const lineas = [];
    this.resultados
      .filter(resultado => resultado.disponible && resultado.seleccionada)
      .forEach(resultado => {
        (resultado.selectedVariants || []).forEach(variante => {
          if (!variante.quantityToAdd) return;
          lineas.push({
            variant_id: DeckBuilder.idNumerico(variante.id),
            cantidad: variante.quantityToAdd,
            nombre: resultado.name
          });
        });
      });
    return lineas;
  }

  abrirBounty() {
    if (!window.VaultBounty) return;
    window.VaultBounty.abrir({
      origen: 'lista',
      cartas: this.cartasParaBounty(),
      cotizacion: this.bounty.cotizacion
    });
  }

  /**
   * "No la tenemos" de verdad.
   *
   * Se distingue de "no cabe más porque ya es tuya", que NO es un faltante y no
   * se puede pintar como uno: el nombre tachado y la miniatura en gris son el
   * lenguaje de "no la tenemos", y aplicárselo a una carta que el usuario acaba
   * de meter a su carrito es la misma mentira que decía el mensaje de error.
   */
  static esFaltante(resultado) {
    return resultado.seleccionada !== false && !resultado.disponible && !resultado.enCarrito;
  }

  /**
   * Por qué esta carta no se puede llevar entera.
   *
   * "No disponible" era la única respuesta para tres situaciones distintas, y
   * en dos de ellas era falsa. La peor: la carta ya estaba en su carrito. Decirle
   * "no disponible" a alguien que la acaba de meter es mandarlo a buscarla a
   * otra tienda con la carta ya apartada.
   */
  etiquetaDeEstado(resultado) {
    if (resultado.disponible) {
      // Se llevó lo que había, pero no todo lo que pidió, y parte de la razón
      // es que el resto ya es suyo. Sin esta línea el "1/3x" no se explica.
      return resultado.enCarrito > 0
        ? `<span class="deck-builder__ya-en-carrito">${resultado.enCarrito} ya en tu carrito</span>`
        : '';
    }

    if (resultado.enCarrito > 0) {
      return `<span class="deck-builder__ya-en-carrito">Ya tienes ${resultado.enCarrito} en tu carrito</span>`;
    }

    // "No la tenemos" y "no la tenemos como la pediste" son cosas distintas, y
    // confundirlas hace que el usuario se vaya a buscarla a otro lado
    // teniéndola nosotros.
    if (resultado.ocultasPorFiltro > 0) {
      return `<span class="not-found-label">Solo en versiones que filtraste (${resultado.ocultasPorFiltro})</span>`;
    }

    return '<span class="not-found-label">No disponible</span>';
  }

  /**
   * Cuántas quieres de esta carta.
   *
   * No existía: la lista solo tenía un checkbox, o sea todo o nada. Cuando de
   * tres copias hay dos, "todo o nada" obliga a tirar la carta entera para poder
   * avanzar. El control se pinta aunque no haya stock libre —con el tope en lo
   * que su lista pedía— para que quitar algo del carrito y volver aquí no exija
   * volver a buscar.
   */
  pintarCantidad(resultado, indice) {
    const tope = Math.max(resultado.topeDisponible || 0, resultado.quantity);
    if (tope <= 1) return '';

    const cantidad = this.cantidadDe(resultado);

    return `
      <div class="deck-builder__cantidad">
        <span class="deck-builder__cantidad-etiqueta" id="deck-cantidad-${indice}">Cantidad</span>
        <button
          type="button"
          class="deck-builder__paso"
          data-indice="${indice}"
          data-paso="-1"
          data-foco="menos-${indice}"
          aria-label="Una menos de ${escaparHtml(resultado.name)}"
          ${cantidad <= 1 ? 'disabled' : ''}
        >&minus;</button>
        <input
          type="number"
          class="deck-builder__cantidad-campo"
          data-cantidad
          data-indice="${indice}"
          data-foco="cantidad-${indice}"
          aria-labelledby="deck-cantidad-${indice}"
          value="${cantidad}"
          min="1"
          max="${tope}"
          inputmode="numeric"
        >
        <button
          type="button"
          class="deck-builder__paso"
          data-indice="${indice}"
          data-paso="1"
          data-foco="mas-${indice}"
          aria-label="Una más de ${escaparHtml(resultado.name)}"
          ${cantidad >= tope ? 'disabled' : ''}
        >+</button>
      </div>
    `;
  }

  /**
   * El selector de versiones.
   *
   * La elección de qué impresión, condición e idioma te vendemos era automática
   * e invisible: la única pista era un `data-tooltip`, que en un teléfono no
   * existe. Aquí se muestra cuál se eligió y cuáles más había, con su precio y
   * su stock, y se puede cambiar.
   *
   * Lo que ofrece son las versiones que devolvió la búsqueda de Shopify, que es
   * un motor de texto sobre títulos: puede traer impresiones de más y no
   * distingue "otra versión de esta carta" de "otra carta que se le parece".
   * Arreglar de dónde sale la lista es ADR 158; poder verla y corregirla es
   * esto, y ya deja al usuario salvarse de una mala elección automática.
   */
  pintarVersiones(resultado, indice) {
    const candidatas = resultado.candidatas || [];
    if (!resultado.disponible || candidatas.length < 2) return '';

    const elegidaId = String((resultado.selectedVariants[0] || {}).id);
    const abierta = resultado.versionesAbiertas;

    return `
      <div class="deck-builder__versiones">
        <button
          type="button"
          class="deck-builder__versiones-toggle"
          data-indice="${indice}"
          data-foco="versiones-${indice}"
          aria-expanded="${abierta ? 'true' : 'false'}"
        >
          ${abierta ? 'Ocultar versiones' : `Ver otras versiones (${candidatas.length - 1})`}
        </button>
        ${!abierta ? '' : `
          <ul class="deck-builder__versiones-lista">
            ${candidatas.map(version => {
              const esElegida = String(version.id) === elegidaId;
              // Cada impresión se ve distinta —arte alterno, borderless, Secret
              // Lair— y en una lista de doce el nombre no alcanza para
              // distinguirlas. La miniatura es el dato que de verdad decide.
              const imagen = version.product.featured_image && version.product.featured_image.url;
              return `
                <li>
                  <button
                    type="button"
                    class="deck-builder__version ${esElegida ? 'is-elegida' : ''}"
                    data-indice="${indice}"
                    data-version-id="${version.id}"
                    data-foco="version-${indice}-${version.id}"
                    ${esElegida ? 'aria-current="true"' : ''}
                  >
                    <span class="deck-builder__version-miniatura">
                      ${imagen
                        ? `<img src="${escaparHtml(imagen)}" alt="" loading="lazy" decoding="async">`
                        : ''}
                    </span>
                    <span class="deck-builder__version-nombre">${escaparHtml(version.product.title)}</span>
                    <span class="deck-builder__version-meta">${escaparHtml(version.title)} · ${version.inventory_quantity} en stock</span>
                    <span class="deck-builder__version-precio">$${(version.price / 100).toFixed(2)}</span>
                  </button>
                </li>
              `;
            }).join('')}
          </ul>
        `}
      </div>
    `;
  }

  /**
   * La reja muestra lo que te vas a llevar, no lo que se encontró. Por eso solo
   * pinta lo seleccionado: quitar una carta —con el tache o con el checkbox—
   * tiene que verse aquí, que es donde el usuario mira el mazo.
   */
  pintarReja() {
    const seleccionadas = this.resultados
      .map((resultado, indice) => ({ resultado, indice }))
      .filter(({ resultado }) => resultado.disponible && resultado.seleccionada);

    if (seleccionadas.length === 0) {
      this.cardsGrid.innerHTML = this.resultados.length
        ? '<div class="error-message">No hay cartas seleccionadas</div>'
        : '<div class="error-message">No se encontraron cartas disponibles</div>';
      return;
    }

    this.cardsGrid.innerHTML = `
      <div class="deck-builder__cards-section">
        ${seleccionadas.map(({ resultado, indice }) => {
          const imagen = resultado.product.featured_image && resultado.product.featured_image.url;
          return `
            <div class="deck-builder__card">
              <img src="${escaparHtml(imagen || '')}" alt="${escaparHtml(resultado.product.title)}">
              <div class="deck-builder__card-quantity">${resultado.availableQuantity}x</div>
              <button
                type="button"
                class="deck-builder__card-remove"
                data-indice="${indice}"
                data-foco="tache-${indice}"
                aria-label="Quitar ${escaparHtml(resultado.name)} de la lista"
                title="Quitar de la lista"
              >&times;</button>
            </div>
          `;
        }).join('')}
      </div>
    `;
  }

  pintarResumen() {
    this.bountySlot.innerHTML = this.pintarBloqueBounty();
    // Con la card de las dos salidas, el resumen de siempre sobra (la card ya
    // dice el monto de hoy y lo que falta) y el botón de carrito pasa a
    // secundario. Lo resuelve el CSS con esta clase.
    if (this.barra) this.barra.classList.toggle('deck-builder__barra--salidas', this.bountySlot.innerHTML !== '');
    let total = 0;
    let cartasSeleccionadas = 0;
    let cartasPedidas = 0;
    let yaEnCarrito = 0;

    this.resultados.forEach(resultado => {
      // Una carta desmarcada salió de la lista: ni se pide ni falta.
      if (resultado.seleccionada === false) return;
      cartasPedidas += resultado.quantity;
      // Lo que ya trae en el carrito no se cuenta como faltante: se cuenta
      // aparte. Meterlo en "faltan" fue justo el error que hacía que la
      // pantalla contradijera al carrito.
      yaEnCarrito += Math.min(resultado.enCarrito || 0, resultado.quantity);
      // La otra versión que agregó desde la lista también ya está en su carrito.
      if (resultado.cambiadaPor) yaEnCarrito += resultado.cambiadaPor.copias;
      if (!resultado.disponible || !resultado.seleccionada) return;
      cartasSeleccionadas += resultado.availableQuantity || 0;
      (resultado.selectedVariants || []).forEach(variante => {
        total += variante.price * variante.quantityToAdd;
      });
    });

    const totalPrecio = document.querySelector('.deck-builder__total-price');
    if (totalPrecio) totalPrecio.textContent = `$${(total / 100).toFixed(2)}`;

    // Antes esta línea decía "Valor total" y multiplicaba el precio por la
    // cantidad PEDIDA en vez de la disponible, así que podía salir por debajo
    // del total estimado: dos cifras del mismo carrito contradiciéndose en
    // pantalla. Lo que sí le sirve a quien cotiza es cuánto le falta.
    const faltantes = document.querySelector('.deck-builder__total-value');
    if (faltantes) {
      const faltan = Math.max(0, cartasPedidas - cartasSeleccionadas - yaEnCarrito);
      const partes = [];
      partes.push(faltan > 0 ? `Faltan ${faltan} de ${cartasPedidas} cartas` : 'Tu lista completa');
      if (yaEnCarrito > 0) partes.push(`${yaEnCarrito} ya en tu carrito`);
      faltantes.textContent = partes.join(' · ');
    }

    // Estos dos `<span>` viven en el markup de la sección. El código anterior
    // les escribía y tres líneas después reemplazaba el `<h2>` entero, así que
    // los destruía: a partir de la segunda búsqueda el contador se quedaba
    // congelado con el número de la primera.
    const cuentaDisponibles = document.querySelector('.deck-builder__available-count');
    if (cuentaDisponibles) cuentaDisponibles.textContent = cartasSeleccionadas;

    const cuentaTotal = document.querySelector('.deck-builder__total-count');
    if (cuentaTotal) cuentaTotal.textContent = cartasPedidas;

    this.addToCartButton.disabled = cartasSeleccionadas === 0;
    this.pintarBotonCarrito();
    if (this.guardarButton) this.guardarButton.disabled = cartasSeleccionadas === 0;

    if (this.copyMissingButton) {
      this.copyMissingButton.hidden = !this.faltantes().length;
    }

    // Los controles solo existen cuando hay algo que controlar.
    if (this.preferenciasElemento) {
      this.preferenciasElemento.hidden = this.resultados.length === 0;
    }
  }

  /**
   * Todo lo que la pantalla tiene que decir: el resultado del último envío al
   * carrito y las líneas que el parser no pudo leer.
   *
   * Los dos viven en el mismo recuadro y por eso los pinta el mismo método.
   * Antes no era así —`mostrarError` escribía por su cuenta en el mismo nodo— y
   * cualquier repintado posterior le borraba el mensaje al usuario.
   */
  pintarAvisos() {
    if (!this.avisoElemento) return;

    const bloques = [this.bloqueDeEnvio(), this.bloqueDeIgnoradas()].filter(Boolean);

    this.avisoElemento.hidden = bloques.length === 0;
    this.avisoElemento.innerHTML = bloques.join('');
  }

  bloqueDeIgnoradas() {
    const ignoradas = this.lineasIgnoradas || [];
    if (ignoradas.length === 0) return '';

    return `
      <p class="deck-builder__aviso-titulo">
        No pudimos leer ${ignoradas.length} ${ignoradas.length === 1 ? 'línea' : 'líneas'} de tu lista:
      </p>
      <ul class="deck-builder__aviso-lista">
        ${ignoradas.map(linea => `<li>${escaparHtml(linea)}</li>`).join('')}
      </ul>
    `;
  }

  /**
   * El resumen de lo que pasó al agregar.
   *
   * Su trabajo es informar SIN cerrar el paso: siempre que haya algo en el
   * carrito ofrece entrar a él. La versión anterior decía "estas cartas ya no
   * estaban disponibles" y ahí terminaba — el usuario se quedaba en una
   * pantalla que ya había metido media lista a su carrito, sin decírselo y sin
   * enseñarle la salida.
   */
  bloqueDeEnvio() {
    const envio = this.ultimoEnvio;
    if (!envio) return '';

    if (envio.mensaje) {
      return `<p class="deck-builder__aviso-titulo">${escaparHtml(envio.mensaje)}</p>`;
    }

    const titulo = envio.unidadesAgregadas > 0
      ? `Agregamos ${envio.unidadesAgregadas} ${envio.unidadesAgregadas === 1 ? 'carta' : 'cartas'} a tu carrito.`
      : 'No pudimos agregar estas cartas:';

    const explicaciones = DeckBuilder.MOTIVOS
      .map(({ clave, texto }) => {
        const nombres = Array.from(new Set(
          (envio.fallidas || [])
            .filter(fallida => fallida.motivo === clave)
            .map(fallida => fallida.nombre)
        ));
        return nombres.length ? `<li>${texto(nombres.length)}: ${escaparHtml(nombres.join(', '))}.</li>` : '';
      })
      .filter(Boolean);

    // El enlace al carrito es el punto: si ya hay algo adentro, el siguiente
    // paso existe y se ve, pase lo que pase con el resto de la lista.
    const irAlCarrito = this.enCarrito.size > 0
      ? '<a class="deck-builder__ir-al-carrito" href="/cart">Ir al carrito &rarr;</a>'
      : '';

    return `
      <div class="deck-builder__envio ${envio.unidadesAgregadas > 0 ? 'is-parcial' : 'is-fallido'}">
        <p class="deck-builder__aviso-titulo">${escaparHtml(titulo)}</p>
        ${explicaciones.length ? `<ul class="deck-builder__aviso-lista">${explicaciones.join('')}</ul>` : ''}
        ${irAlCarrito}
      </div>
    `;
  }

  /**
   * Qué decirle al usuario por cada motivo de rechazo, en orden de utilidad.
   *
   * Los cuatro caían antes en la misma frase —"ya no estaban disponibles"—, que
   * para tres de ellos era mentira y para ninguno decía qué hacer.
   */
  static get MOTIVOS() {
    return [
      {
        clave: 'carrito',
        texto: n => `Ya ${n === 1 ? 'la tienes' : 'las tienes'} en tu carrito, con todo lo que teníamos`
      },
      {
        clave: 'agotada',
        texto: n => `Se ${n === 1 ? 'agotó' : 'agotaron'} mientras armabas tu lista`
      },
      {
        clave: 'limite',
        texto: () => 'La tienda nos pidió ir más despacio; vuelve a darle a Agregar al carrito para estas'
      },
      {
        clave: 'red',
        texto: () => 'No pudimos agregarlas, revisa tu conexión'
      }
    ];
  }

  /**
   * Las que de verdad no tenemos. Una carta que no se puede agregar porque ya
   * está en tu carrito no es un faltante: copiarla a la lista que le vas a
   * llevar a otra tienda sería mandarte a comprar dos veces la misma.
   */
  faltantes() {
    return this.resultados.filter(DeckBuilder.esFaltante);
  }

  copiarFaltantes() {
    const faltantes = this.faltantes()
      .map(resultado => `${resultado.quantity} ${resultado.name}`)
      .join('\n');

    if (!faltantes) return;

    navigator.clipboard.writeText(faltantes).then(() => {
      const etiqueta = this.copyMissingButton.querySelector('.deck-builder__copy-missing-label');
      if (!etiqueta) return;
      const original = etiqueta.textContent;
      etiqueta.textContent = '✓ Copiado';
      setTimeout(() => { etiqueta.textContent = original; }, 2000);
    });
  }

  /** Con faltantes el carrito es la opción "solo lo que hay"; sin ellas, la única. */
  /**
   * El botón de carrito es el que la gente ya usa: se queda como primary y su
   * precio es lo más visible de la pantalla. Texto a la izquierda, monto
   * grande a la derecha. Solo números y texto fijo: nada del usuario.
   */
  pintarBotonCarrito() {
    const hay = this.unidadesDisponibles();
    const faltan = this.cartasParaBounty().length > 0;
    const texto = hay === 0
      ? 'Agregar al carrito'
      : faltan
        ? 'Me llevo solo lo que hay'
        : (hay === 1 ? 'Agregar 1 al carrito' : `Agregar ${hay} al carrito`);
    const precio = hay > 0 && window.VaultBounty ? window.VaultBounty.pesos(this.montoDisponible() / 100) : '';
    const detalle = hay > 0 ? `${hay} ${hay === 1 ? 'carta' : 'cartas'}` : '';

    this.addToCartButton.classList.toggle('deck-builder__add-to-cart--precio', Boolean(precio));
    this.addToCartButton.setAttribute('aria-label', precio ? `${texto}: ${precio}` : texto);
    this.addToCartButton.innerHTML = precio
      ? `<span class="deck-builder__carrito-texto"><span class="deck-builder__carrito-accion">${escaparHtml(texto)}</span><span class="deck-builder__carrito-detalle">${escaparHtml(detalle)}</span></span><span class="deck-builder__carrito-precio">${escaparHtml(precio)}</span>`
      : escaparHtml(texto);
  }

  /**
   * ADR 251 (CardVault): guarda lo elegido como carrito con nombre, sin tocar
   * el carrito de Shopify. Mismas líneas que `addToCart`, leídas del estado.
   */
  guardarCarrito() {
    if (!window.VaultCarritoGuardado) return;
    const lineas = this.lineasElegidas();
    const nombres = lineas.map(l => l.nombre).filter(Boolean);
    const porDefecto = nombres.length ? (nombres.length > 1 ? `${nombres[0]} y ${nombres.length - 1} más` : nombres[0]).slice(0, 40) : '';
    window.VaultCarritoGuardado.guardarLista(lineas, porDefecto);
  }

  /** Las líneas elegidas, en el formato de los carritos guardados (ADR 251). */
  lineasElegidas() {
    const lineas = [];
    this.resultados
      .filter(resultado => resultado.disponible && resultado.seleccionada)
      .forEach(resultado => {
        (resultado.selectedVariants || []).forEach(variante => {
          if (!variante.quantityToAdd) return;
          const imagen = variante.product && variante.product.featured_image && variante.product.featured_image.url;
          lineas.push({
            variantId: String(variante.id),
            cantidad: variante.quantityToAdd,
            nombre: resultado.name,
            imagen: typeof imagen === 'string' ? (imagen.startsWith('//') ? `https:${imagen}` : imagen) : undefined,
          });
        });
      });
    return lineas;
  }

  async addToCart() {
    // Lee del estado, no del DOM. Antes las variantes viajaban serializadas en
    // un `data-variants` de cada checkbox y se volvían a parsear aquí: el DOM
    // hacía de base de datos, y por eso desmarcar no afectaba a nada más que a
    // este momento.
    const seleccionadas = this.resultados.filter(
      resultado => resultado.disponible && resultado.seleccionada
    );

    if (seleccionadas.length === 0) {
      this.mostrarError('Selecciona al menos una carta para agregar al carrito.');
      return;
    }

    // ADR 251 (CardVault): con carritos guardados, se pregunta a cuál van.
    // 'actual' sigue el camino de siempre; 'hecho' ya quedaron guardadas.
    if (window.VaultCarritoGuardado) {
      const destino = await window.VaultCarritoGuardado.elegirDestino(this.lineasElegidas(), 'lista');
      if (destino !== 'actual') return;
    }

    this.addToCartButton.setAttribute('disabled', '');
    this.addToCartButton.textContent = 'Agregando...';

    const items = [];
    const nombrePorItem = new Map();

    seleccionadas.forEach(resultado => {
      (resultado.selectedVariants || []).forEach(variante => {
        const item = {
          id: variante.id,
          quantity: variante.quantityToAdd,
          properties: {
            '_Card_Name': variante.product.title,
            '_Condition': variante.title
          }
        };
        items.push(item);
        nombrePorItem.set(item, resultado.name);
      });
    });

    let fallidas;

    try {
      // Camino rápido: `/cart/add.js` acepta el lote completo en UNA petición.
      // Antes se mandaba un POST por variante Y EN SERIE: un deck de 40 cartas
      // eran 40 viajes de ida y vuelta encadenados.
      const respuesta = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      // El lote es atómico: si una carta se cayó entre la búsqueda y el clic,
      // Shopify rechaza las 40. Ahí se va de una en una para que el usuario se
      // lleve lo que sí quedaba y sepa exactamente qué no llegó.
      fallidas = respuesta.ok ? [] : await this.agregarUnaPorUna(items);
    } catch (error) {
      console.error('Error agregando al carrito:', error);
      fallidas = items.map(item => ({ item, motivo: 'red' }));
    } finally {
      this.addToCartButton.removeAttribute('disabled');
      this.pintarBotonCarrito();
    }

    const unidadesAgregadas = items
      .filter(item => !fallidas.some(fallida => fallida.item === item))
      .reduce((suma, item) => suma + item.quantity, 0);

    // NORTH STAR. Se mide el carrito que de verdad se llenó, no el clic. Antes
    // solo contaba el éxito total: un envío parcial —que para el usuario ES un
    // carrito— no aparecía en la métrica.
    if (unidadesAgregadas > 0) medir('deck_add_to_cart', unidadesAgregadas, { cartas: unidadesAgregadas });

    if (fallidas.length === 0) {
      window.location.href = '/cart';
      return;
    }

    // Algo no entró. Antes esto era el final del camino: un mensaje rojo y
    // nada más — sin enlace al carrito, y con el botón rearmado para que el
    // siguiente clic DUPLICARA lo que ya había entrado.
    //
    // Ahora se vuelve a leer el carrito y se recalcula todo contra él. Con eso
    // la pantalla queda diciendo la verdad, un segundo clic ya no puede
    // duplicar —lo que ya es tuyo no vuelve a caber— y el resumen ofrece el
    // carrito como siguiente paso. Se informa, no se bloquea.
    await this.cargarCarrito();

    // Shopify acaba de decir que estas se agotaron. El catálogo todavía cree
    // que hay —se consultó hace un minuto y tiene caché— así que sin esto la
    // línea seguiría ofreciendo una carta que ya no existe, y el siguiente clic
    // volvería a chocar contra el mismo muro.
    const agotadas = new Set(
      fallidas.filter(fallida => fallida.motivo === 'agotada').map(fallida => String(fallida.item.id))
    );

    if (agotadas.size > 0) {
      this.resultados.forEach(resultado => {
        (resultado.versiones || []).forEach(version => {
          if (agotadas.has(String(version.id))) version.inventory_quantity = 0;
        });
      });
    }

    // Lo que se agotó al agregar sigue marcado: ahora falta, y se pide al Bounty.
    this.recalcularTodo();

    this.ultimoEnvio = {
      unidadesAgregadas,
      fallidas: fallidas.map(fallida => ({
        motivo: fallida.motivo,
        nombre: nombrePorItem.get(fallida.item) || fallida.item.properties['_Card_Name']
      }))
    };
    this.pintar();
    if (this.avisoElemento) this.avisoElemento.scrollIntoView({ block: 'nearest' });
  }

  /**
   * Una por una, EN SERIE. Antes iban seis en paralelo y ahí estaba el segundo
   * bug: `/cart/add.js` está limitado por tasa, contestaba 429, y un 429 se
   * reportaba al usuario como "esta carta ya no está disponible".
   *
   * Devuelve `{ item, motivo }` de las que no entraron — el motivo es lo que
   * permite decirle al usuario algo cierto en vez de "se agotó" para todo.
   */
  async agregarUnaPorUna(items) {
    const fallidas = [];

    for (const item of items) {
      const motivo = await this.agregarUna(item);
      if (motivo) fallidas.push({ item, motivo });
    }

    return fallidas;
  }

  /** `null` si entró; si no, el motivo. Reintenta solo el 429. */
  async agregarUna(item, intento) {
    const vuelta = intento || 0;
    let respuesta;

    try {
      respuesta = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item] })
      });
    } catch (error) {
      return 'red';
    }

    if (respuesta.ok) return null;

    if (respuesta.status === 429) {
      if (vuelta >= DeckBuilder.REINTENTOS) return 'limite';
      await DeckBuilder.esperar(DeckBuilder.ESPERA_BASE * Math.pow(2, vuelta));
      return this.agregarUna(item, vuelta + 1);
    }

    return DeckBuilder.motivoDelRechazo(respuesta);
  }

  /**
   * Por qué Shopify dijo que no.
   *
   * "Tu carrito ya tiene la cantidad máxima de este artículo" y "se agotó" son
   * el mismo 422 y significan cosas opuestas: en el primero la carta está
   * apartada para ti. Confundirlos es lo que mandaba a la gente a buscar a otra
   * tienda una carta que ya tenía en su propio carrito.
   */
  static async motivoDelRechazo(respuesta) {
    let mensaje = '';

    try {
      const cuerpo = await respuesta.json();
      mensaje = String((cuerpo && (cuerpo.description || cuerpo.message)) || '');
    } catch (error) {
      // Cuerpo ilegible: se cae al motivo genérico.
    }

    // La tienda contesta en su idioma, y el tema corre en español y en inglés.
    return /m[áa]xima|maximum|already in your cart/i.test(mensaje) ? 'carrito' : 'agotada';
  }

  /**
   * Los errores se dicen en la página, no en un `alert()`. El alert bloquea,
   * se ve como un aviso del navegador y no deja copiar ni releer lo que dice —
   * y aquí lo que dice suele ser una lista de cartas.
   */
  mostrarError(mensaje) {
    if (!this.avisoElemento) return;
    this.ultimoEnvio = { mensaje };
    this.pintarAvisos();
    this.avisoElemento.scrollIntoView({ block: 'nearest' });
  }
}


document.addEventListener('DOMContentLoaded', () => {
  new DeckBuilder();
});